import React, { useState } from 'react';
import { Modal, Form, Input, message } from 'antd';
import { LockOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { POST } from '../utils/apiRequest';
import API from '../config/apis';

interface ChangePasswordModalProps {
    open: boolean;
    onClose: () => void;
}

const PASSWORD_REGEX = /^(?=.*[A-Z]).{8,}$/;

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ open, onClose }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const res = await POST(API.CHANGE_PASSWORD, {
                oldPassword: values.oldPassword,
                newPassword: values.newPassword,
            });

            if (res.status || res.message === 'Password changed successfully') {
                message.success('Password changed successfully');
                form.resetFields();
                onClose();
            } else {
                const errMsg = res.data?.message || res.message || 'Failed to change password';
                message.error(Array.isArray(errMsg) ? errMsg.join(', ') : errMsg);
            }
        } catch (error: any) {
            if (error?.errorFields) return;
            message.error(error?.message || 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onClose();
    };

    return (
        <Modal
            title={
                <div className="flex items-center gap-2">
                    <LockOutlined className="text-primary" />
                    <span>Change Password</span>
                </div>
            }
            open={open}
            onOk={handleSubmit}
            onCancel={handleCancel}
            okText="Update Password"
            okButtonProps={{ loading }}
            destroyOnHidden
        >
            <p className="text-xs text-muted-foreground mb-4">
                Enter your current password and choose a new one.
            </p>

            <Form form={form} layout="vertical" requiredMark={false}>
                <Form.Item
                    name="oldPassword"
                    label={<span className="text-sm font-medium">Current Password</span>}
                    rules={[{ required: true, message: 'Please enter your current password' }]}
                >
                    <Input.Password
                        placeholder="Enter current password"
                        size="large"
                        iconRender={(visible) => visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
                    />
                </Form.Item>

                <Form.Item
                    name="newPassword"
                    label={<span className="text-sm font-medium">New Password</span>}
                    rules={[
                        { required: true, message: 'Please enter a new password' },
                        {
                            pattern: PASSWORD_REGEX,
                            message: 'Min 8 characters with at least 1 uppercase letter',
                        },
                    ]}
                    extra={
                        <span className="text-[10px] text-muted-foreground">
                            Min 8 characters, 1 uppercase letter
                        </span>
                    }
                >
                    <Input.Password
                        placeholder="Enter new password"
                        size="large"
                        iconRender={(visible) => visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
                    />
                </Form.Item>

                <Form.Item
                    name="confirmPassword"
                    label={<span className="text-sm font-medium">Confirm New Password</span>}
                    dependencies={['newPassword']}
                    rules={[
                        { required: true, message: 'Please confirm your new password' },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('newPassword') === value) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error('Passwords do not match'));
                            },
                        }),
                    ]}
                >
                    <Input.Password
                        placeholder="Confirm new password"
                        size="large"
                        iconRender={(visible) => visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ChangePasswordModal;
